import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Calculator, CheckCircle2, CreditCard, Loader2, Phone, Plus, Trash2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { useVehiclePrices } from "@/lib/useVehiclePrices";
import { SMS_CONSENT_TEXT } from "@/lib/smsConsent";
import { AddressInput } from "@/components/AddressInput";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };

export default function PublicBooking() {
  const { companyId } = useParams();
  const [info, setInfo] = useState(null);
  const [booking, setBooking] = useState(null);
  const [quote, setQuote] = useState(null);
  const [miles, setMiles] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [routes, setRoutes] = useState([]);
  const [search] = useSearchParams();
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "", sms_consent: false, save_card: false,
    pickup: emptyPlace, dropoff: emptyPlace, stops: [],
    vehicle_id: "", pricing_mode: "mileage", hours: 3,
    trip_date: "", pickup_time: "", passengers: 1, vehicle_pref: "",
    airline: "", flight_number: "", terminal: "", notes: "",
  });

  useEffect(() => {
    api.get(`/public/company/${companyId}`).then(({ data }) => setInfo(data)).catch((e) => setError(errMsg(e)));
  }, [companyId]);

  const cid = info?.company?.id || companyId;

  useEffect(() => {
    if (!cid) return;
    api.get(`/public/company/${cid}/flat-rates`).then(({ data }) => setRoutes(data.routes)).catch(() => {});
  }, [cid]);

  // Arriving from a shared route link — start with the trip already filled in.
  useEffect(() => {
    const pickup = search.get("pickup");
    const dropoff = search.get("dropoff");
    if (!pickup && !dropoff) return;
    setForm((f) => ({ ...f,
      pickup: pickup ? { ...emptyPlace, address: pickup, formatted_address: pickup } : f.pickup,
      dropoff: dropoff ? { ...emptyPlace, address: dropoff, formatted_address: dropoff } : f.dropoff }));
  }, [search]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const vehicle = (info?.vehicles || []).find((v) => v.id === form.vehicle_id);
  const points = useMemo(() => [form.pickup, ...form.stops, form.dropoff], [form.pickup, form.stops, form.dropoff]);

  const getQuote = async () => {
    if (!form.vehicle_id) { setError("Choose a vehicle to see your price."); return; }
    setBusy("quote");
    setError("");
    try {
      const { data } = await api.post(`/public/company/${cid}/quote`, {
        vehicle_id: form.vehicle_id, pricing_mode: form.pricing_mode,
        pickup: form.pickup, dropoff: form.dropoff, stops: form.stops,
        hours: Number(form.hours || 0), miles: null, customer_email: form.customer_email,
      });
      setMiles(data.miles ?? null);
      setQuote(data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  // Every vehicle is priced on Google's driving distance the moment both ends are known.
  const { prices, miles: pricedMiles, loading: pricing } = useVehiclePrices({
    companyId: cid, pickup: form.pickup, dropoff: form.dropoff, stops: form.stops,
    hours: form.hours, pricingMode: form.pricing_mode, email: form.customer_email,
  });

  useEffect(() => {
    setQuote(prices[form.vehicle_id] || null);
    if (pricedMiles != null) setMiles(pricedMiles);
  }, [prices, form.vehicle_id, pricedMiles]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy("submit");
    setError("");
    try {
      const { data } = await api.post(`/public/company/${cid}/bookings`, {
        ...form,
        passengers: Number(form.passengers || 1),
        hours: Number(form.hours || 0),
        miles: null,
        stops: form.stops.map(({ address, formatted_address, place_id, lat, lng }) => ({ address, formatted_address, place_id, lat, lng })),
      });
      setBooking(data);
      if (Number(data.deposit_due || 0) > 0 && info?.company?.online_payments_ready
          && data.pay_before_approval !== false) {
        payDeposit(data.id);
      }
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const payDeposit = async (bookingId) => {
    setBusy("pay");
    setError("");
    try {
      const { data } = await api.post(`/public/bookings/${bookingId || booking.id}/deposit-checkout`, {
        origin_url: window.location.origin,
      });
      if (!openCheckout(data.checkout_url, setCheckoutUrl)) { setCheckoutUrl(data.checkout_url); setBusy(""); }
    } catch (e) { setError(errMsg(e)); setBusy(""); }
  };

  if (!info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        {error ? <p data-testid="booking-page-error" className="text-sm text-red-400">{error}</p> : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const c = info.company;
  const field = "h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold";

  return (
    <div data-testid="public-booking-page" className="min-h-screen bg-ink-900 text-slate-200">
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="rounded-t-xl bg-ink-800 p-7" style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 text-2xl font-bold text-slate-50 sm:text-3xl">Reserve your car</h1>
              {(c.contact_phone || c.phone) && (
                <a data-testid="booking-contact-phone" href={`tel:${(c.contact_phone || c.phone).replace(/[^\d+]/g, "")}`}
                  className="mt-2 flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-gold">
                  <Phone className="h-3 w-3" /> {c.contact_phone || c.phone}
                </a>
              )}
              {c.contact_hours && (
                <p data-testid="booking-contact-hours" className="mt-1 text-xs text-slate-500">{c.contact_hours}</p>
              )}
            </div>
            {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} className="h-14 w-auto object-contain" />}
          </div>
        </div>

        <div className="rounded-b-xl border border-t-0 border-ink-600 bg-ink-800/60 p-7">
          {booking ? (
            <div data-testid="booking-confirmation" className="space-y-5 py-4 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
              <h2 className="text-2xl font-bold text-slate-50">Request received</h2>
              <p className="text-sm text-slate-400">
                {c.name} will confirm your trip by email shortly. A copy of your request is on its way to you.
              </p>
              {booking.deposit_due > 0 && c.online_payments_ready && (
                <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-5 text-left">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Secure your reservation</p>
                  <p className="mt-2 text-sm text-slate-300">
                    Estimated total {money(booking.estimated_total)} · deposit due now{" "}
                    <span className="font-semibold text-slate-50">{money(booking.deposit_due)}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Hourly and mileage trips are finalised after the ride; any balance is charged then.
                  </p>
                  <Button data-testid="pay-deposit-btn" disabled={busy === "pay"} onClick={payDeposit} className="mt-4 h-14 w-full bg-gold text-base text-ink-900 hover:bg-gold-light">
                    {busy === "pay" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CreditCard className="mr-2 h-4 w-4" /> Pay {money(booking.deposit_due)} deposit</>)}
                  </Button>
                  {c.payments_sandbox && (
                    <div className="mt-3">
                      <SandboxNotice testId="booking-sandbox" />
                    </div>
                  )}
                </div>
              )}
              {error && <p data-testid="deposit-error" className="text-sm text-red-400">{error}</p>}
              {checkoutUrl && (
                <a data-testid="booking-checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                  className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-center text-sm font-semibold text-gold">
                  Open the secure card form →
                </a>
              )}
            </div>
          ) : (
            <form data-testid="public-booking-form" onSubmit={submit}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }}
              className="space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Input data-testid="booking-name" required placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} className={field} />
                <Input data-testid="booking-email" required type="email" placeholder="Email" value={form.customer_email} onChange={set("customer_email")} className={field} />
                <Input data-testid="booking-phone" placeholder="Phone" value={form.customer_phone} onChange={set("customer_phone")} className={field} />
                <label data-testid="booking-sms-consent-label" className="flex cursor-pointer items-start gap-3 sm:col-span-2">
                  <input data-testid="booking-sms-consent" type="checkbox" checked={form.sms_consent}
                    onChange={(e) => setForm((f) => ({ ...f, sms_consent: e.target.checked }))}
                    className="mt-1 h-4 w-4 accent-gold" />
                  <span className="text-xs leading-relaxed text-slate-400">
                    {SMS_CONSENT_TEXT} See our{" "}
                    <a href="/legal/sms" target="_blank" rel="noreferrer" className="text-gold hover:underline">SMS terms</a>{" "}
                    and <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-gold hover:underline">privacy policy</a>.
                  </span>
                </label>
                {info?.company?.payment_policy?.auto_charge_balance && info?.company?.online_payments_ready
                  && (info?.company?.payment_processor || "stripe") === "stripe" && (
                  <label data-testid="booking-save-card-label" className="flex cursor-pointer items-start gap-3 sm:col-span-3">
                    <input data-testid="booking-save-card" type="checkbox" checked={form.save_card}
                      onChange={(e) => setForm((f) => ({ ...f, save_card: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-gold" />
                    <span className="text-xs leading-relaxed text-slate-400">
                      Keep my card on file and settle the rest of my fare after the trip — the
                      balance is charged automatically about 30 minutes after we arrive. You can
                      still add a gratuity afterwards.
                    </span>
                  </label>
                )}
              </div>

              {routes.length > 0 && (
                <div data-testid="booking-route-cards">
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-gold">Flat airport rates</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {routes.slice(0, 6).map((r) => (
                      <button type="button" key={`${r.origin}-${r.airport}`}
                        data-testid={`booking-route-${r.origin.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => {
                          setForm((f) => ({ ...f,
                            pickup: { ...emptyPlace, address: r.origin, formatted_address: r.origin },
                            dropoff: { ...emptyPlace, address: r.airport, formatted_address: r.airport } }));
                          setQuote(null);
                        }}
                        className="rounded-xl border border-ink-600 bg-ink-800 p-4 text-left transition-colors hover:border-gold/40">
                        <p className="text-sm text-slate-100">{r.origin} → {r.airport}</p>
                        <p className="mt-1 font-display text-xl text-gold">{money(r.price)}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                          {r.direction === "both" ? "Both ways · flat" : "Flat rate"}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <AddressInput label="Pickup address" testId="booking-pickup" companyId={cid} useMyLocation value={form.pickup} onChange={(p) => { setForm((f) => ({ ...f, pickup: p })); setQuote(null); }} />
              {form.stops.map((s, i) => (
                <div key={i} className="flex items-end gap-3">
                  <div className="flex-1">
                    <AddressInput label={`Stop ${i + 1}`} testId={`booking-stop-${i}`} companyId={cid} value={s}
                      onChange={(p) => { setForm((f) => ({ ...f, stops: f.stops.map((x, xi) => (xi === i ? { ...x, ...p } : x)) })); setQuote(null); }} />
                  </div>
                  <button type="button" data-testid={`booking-stop-remove-${i}`} className="mb-1 text-slate-500 hover:text-red-400"
                    onClick={() => setForm((f) => ({ ...f, stops: f.stops.filter((_, xi) => xi !== i) }))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" data-testid="booking-add-stop"
                onClick={() => setForm((f) => ({ ...f, stops: [...f.stops, { ...emptyPlace }] }))}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                <Plus className="mr-2 h-4 w-4" /> Add stop
              </Button>
              <AddressInput label="Drop-off address" testId="booking-dropoff" companyId={cid} value={form.dropoff} onChange={(p) => { setForm((f) => ({ ...f, dropoff: p })); setQuote(null); }} />

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Vehicle</label>
                  <select
                    data-testid="booking-vehicle"
                    value={form.vehicle_id}
                    onChange={(e) => {
                      const v = (info.vehicles || []).find((x) => x.id === e.target.value);
                      setForm((f) => ({ ...f, vehicle_id: e.target.value, pricing_mode: v?.pricing_mode || "mileage", vehicle_pref: v?.name || "" }));
                      setQuote(null);
                    }}
                    className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
                  >
                    <option value="">Select a vehicle</option>
                    {(info.vehicles || []).filter((v) => (v.pricing_mode === "hourly" ? v.hourly_rate > 0 : v.per_mile_rate > 0 || v.reservation_fee > 0)).map((v) => (
                      <option key={v.id} value={v.id}>
                        {prices[v.id]
                          ? `${v.name} · ${money(prices[v.id].total)}`
                          : v.pricing_mode === "hourly"
                            ? `${v.name} · ${v.minimum_hours}h minimum`
                            : v.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Service</label>
                  <select
                    data-testid="booking-pricing-mode"
                    value={form.pricing_mode}
                    onChange={(e) => { setForm((f) => ({ ...f, pricing_mode: e.target.value })); setQuote(null); }}
                    className="h-12 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
                  >
                    <option value="mileage">Point-to-point</option>
                    <option value="hourly">Hourly charter</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
                <Input data-testid="booking-date" type="date" required value={form.trip_date} onChange={set("trip_date")} className={field} />
                <Input data-testid="booking-time" type="time" required value={form.pickup_time} onChange={set("pickup_time")} className={field} />
                <Input data-testid="booking-passengers" type="number" min="1" value={form.passengers} onChange={set("passengers")} className={field} />
                {form.pricing_mode === "hourly" && (
                  <Input data-testid="booking-hours" type="number" step="0.5" min="1" value={form.hours} onChange={(e) => { set("hours")(e); setQuote(null); }} className={field} />
                )}
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Input data-testid="booking-airline" placeholder="Airline" value={form.airline} onChange={set("airline")} className={field} />
                <Input data-testid="booking-flight" placeholder="Flight number" value={form.flight_number} onChange={set("flight_number")} className={field} />
                <Input data-testid="booking-terminal" placeholder="Terminal" value={form.terminal} onChange={set("terminal")} className={field} />
              </div>

              <Textarea data-testid="booking-notes" rows={3} placeholder="Special instructions" value={form.notes} onChange={set("notes")} className="border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />

              <Button type="button" data-testid="booking-quote-btn" disabled={busy === "quote"} onClick={getQuote} variant="outline"
                className="border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                {busy === "quote" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Calculator className="mr-2 h-4 w-4" /> Recalculate my price</>)}
              </Button>
              {!quote && pricing && (
                <p data-testid="booking-pricing" className="text-xs text-slate-500">Working out your price…</p>
              )}
              {quote && <QuoteBreakdown quote={quote} miles={miles} testId="booking-quote" label="Your price" simple />}
              {vehicle?.pricing_mode === "hourly" && (
                <p className="text-xs text-slate-500">
                  Hourly trips are finalised after the ride: extra mileage beyond {vehicle.included_miles_per_hour} mi/hour,
                  added stops, waiting and overtime are added to the final total.
                </p>
              )}

              {error && <p data-testid="booking-error" className="text-sm text-red-400">{error}</p>}
              <Button data-testid="booking-submit" type="submit" disabled={busy === "submit"} className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
                {busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request this trip"}
              </Button>
              <p className="text-[11px] leading-relaxed text-slate-500">
                <strong className="text-slate-400">Transportation Service Disclaimer</strong> — This transportation
                service is provided by {c.name}, the transportation operator identified on this reservation. The
                software platform facilitates communication, scheduling, dispatch and payment technology but does
                not provide or operate the transportation service.
              </p>
            </form>
          )}
          {c.show_powered_by && (
            <p data-testid="booking-powered-by" className="mt-6 text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
          )}
        </div>
      </div>
    </div>
  );
}
